import {TabList} from "@mui/lab";
import TabPanel from "@mui/lab/TabPanel/TabPanel";
import Plot from "react-plotly.js";
import TabContext from "@mui/lab/TabContext/TabContext";
import * as React from "react";
import {useState} from "react";
import styled from "styled-components";
import * as colors from "../../../../../../../constants/colors";
import {ArgosDownloadIcon} from "../../../../../../components/Icons/ArgosActionIcons";
import {Tab} from '@mui/material';
import {MaterialReactTable} from "material-react-table";
import {roundToXDecimals} from "../../../../../../../helpers/helpers";
import {downloadSensorCsv, downloadSensorCsvRadar} from "../../../../../../../helpers/sensors/helpers";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import {fovAndBoresightRadarKeyToLabelMap, COLUMN_NAMES} from "../../constants";
import {IconButton, Tooltip} from '@mui/material';
import {ArgosTableSimple} from "../../../../../../components/Table/ArgosTableSimple";
import {getMaxFinite} from "../../../../../../../helpers/graphs";
import {getRadarDisplayName} from "./RightPanel";

// Accepts a map of sensorId -> {data, columns}
export const RightPanelPlot = (props) => {
  const [globalMin, setGlobalMin] = useState(null);
  const [globalMax, setGlobalMax] = useState(null);
  const [graphOrTable, setGraphOrTable] = useState(0);

  // props.data is now a map: sensorId -> {data, columns}
  const plotDataMap = props?.plotDataMap ?? {};
  const sensorIds = Object.keys(plotDataMap);

  // Assign a color for each sensor line
  const colorPalette = [
    "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
    "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
  ];

  // Build traces for each sensor
  const traces = sensorIds.map((sensorId, idx) => {
    const d = plotDataMap[sensorId]?.data ?? {};
    const x = (d.x ?? []).map(element => element < 0 ? element + 360 : element);
    const y = d.y ?? [];
    const radar = props.allRadars?.[sensorId];
    return {
      r: y,
      theta: x,
      type: 'scatterpolar',
      mode: 'lines+markers',
      name: radar ? getRadarDisplayName(radar) : sensorId, // Use display name for legend
      line: {shape: 'spline', smoothing: 1.3, color: colorPalette[idx % colorPalette.length]},
      marker: {color: colorPalette[idx % colorPalette.length]}
    }
  });

  // Get all property names from the first sensor in fovAndBoresightRadarValuesMap
  const propertyNames = props.fovAndBoresightRadarValuesMap && sensorIds.length > 0
    ? Object.keys(props.fovAndBoresightRadarValuesMap[sensorIds[0]] || {})
    : [];

  // Build columns: Sensor + property columns
  const fovAndBoresightRadarTableColumns = [
    {header: "Sensor", accessorKey: "sensor", enableEditing: false, size: 180},
    {header: <span>Azimuth<br />FOV (deg)</span>, accessorKey: "azimuth_fov", enableEditing: false, size: 150},
    {header: <span>Elevation<br />FOV (deg)</span>, accessorKey: "elevation_fov", enableEditing: false, size: 150},
    {header: "Max Det. Range at Boresight (m)", accessorKey: "max_range", enableEditing: false, size: 150},
  ];

  // Build rows: one per sensor, with property values
  const fovAndBoresightRadarTableData = sensorIds.map((sensorId) => {
    const values = props.fovAndBoresightRadarValuesMap?.[sensorId] || {};
    const radar = props.allRadars?.[sensorId];
    return {
      sensor: radar ? getRadarDisplayName(radar) : sensorId,
      ...propertyNames.reduce((acc, prop) => {
        acc[prop] = values[prop] !== undefined ? values[prop] : '';
        return acc;
      }, {})
    };
  });

  // Compute global min/max for axis scaling
  React.useEffect(() => {
    let allX = [];
    let allY = [];
    sensorIds.forEach(sensorId => {
      const d = plotDataMap[sensorId]?.data ?? {};
      allX = allX.concat((d.x ?? []).map((v) => Math.abs(v)));
      allY = allY.concat((d.y ?? []).map((v) => Math.abs(v)));
    });
    if (allX.length && allY.length) {
      let absoluteMax = getMaxFinite(allX.concat(allY));
      setGlobalMin((-1) * absoluteMax)
      setGlobalMax(absoluteMax)
    }
  }, [plotDataMap]);

  function handleGraphOrTableChange(e, newValue) {
    setGraphOrTable(newValue)
  }

  // Collect all unique angles
  const angleSet = new Set();
  sensorIds.forEach(sensorId => {
    const d = plotDataMap[sensorId]?.data ?? {};
    (d.x ?? []).forEach(angle => angleSet.add(angle));
  });
  const allAngles = Array.from(angleSet).sort((a, b) => a - b);

  // Build tableData: each row is an angle, each sensor ID is a column
  const tableData = allAngles.map(angle => {
    const row = {angle};
    sensorIds.forEach(sensorId => {
      const d = plotDataMap[sensorId]?.data ?? {};
      const idx = (d.x ?? []).findIndex(x => x === angle);
      row[sensorId] = idx !== -1 ? roundToXDecimals((d.y ?? [])[idx], 3) : null;
    });
    return row;
  });

  // Build plotDataColumns: Angle + one column per sensor ID
  const plotDataColumns = [
    {
      id: 'angle',
      accessorKey: 'angle',
      // header: COLUMN_NAMES.AZIMUTH_ANGLE,
      header: <span>Azimuth<br />Angle<br />(deg)</span>,
      enableEditing: false,
    },
    ...sensorIds.map(sensorId => ({
      id: sensorId,
      accessorKey: sensorId,
      header: (props.allRadars?.[sensorId]
        ? getRadarDisplayName(props.allRadars[sensorId])
        : sensorId) + ' Distance (m)',
      enableEditing: false,
    }))
  ];

  const sensorIdToName = {};
  sensorIds.forEach(sensorId => {
    const radar = props.allRadars?.[sensorId];
    sensorIdToName[sensorId] = radar ? getRadarDisplayName(radar) : sensorId;
  });

  // Download all as CSV
  const actions = (
    <div style={{display: 'flex', justifyContent: 'flex-end', width: '100%'}}>
      <Tooltip title="Download CSV" arrow>
        <IconButton>
          <ArgosDownloadIcon
            onClick={() => downloadSensorCsvRadar(tableData, `${document.title} Performance Data`, sensorIdToName)}/>
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <div style={{display: 'flex', flexFlow: 'row nowrap', overflow: 'auto', height: '100%', alignItems: 'center'}}>
      <TabContext value={graphOrTable}>
        <ArgosFlexbox vertical>
          <div>
            <TabList value={graphOrTable} onChange={handleGraphOrTableChange} aria-label="basic tabs example">
              <Tab sx={{fontSize: 12}} label="Graph" value={0}/>
              <Tab sx={{fontSize: 12}} label="Data Table" value={1}/>
            </TabList>
          </div>
          <div style={{flexGrow: 1, overflow: 'auto'}}>
            <TabPanel style={{height: "100%"}} value={0} index={0}>
              <Plot
                data={traces}
                useResizeHandler={true}
                style={{width: 800, height: 800}}
                layout={{
                  paper_bgcolor: '#f2f4f5',
                  plot_bgcolor: '#f2f4f5',
                  title: 'Radar Performance',
                  shapes: {type: 'path'},
                  autosize: true,
                  polar: {
                    angularaxis: {
                      rotation: 90
                    }
                  },
                  legend: {title: {text: "Sensor ID"}},
                  xaxis: {
                    title: COLUMN_NAMES.AZIMUTH_ANGLE,
                    range: [globalMin, globalMax],
                    nticks: 6,
                    rangemode: "tozero"
                  },
                  yaxis: {title: COLUMN_NAMES.DISTANCE, range: [globalMin, globalMax], nticks: 6, rangemode: "tozero"},
                }}
              />
            </TabPanel>
            <TabPanel value={1} index={1} style={{overflow: 'auto', height: '100%',}}>
              <div style={{padding: "0px", height: 700}}>
                <ArgosTableSimple
                  columns={plotDataColumns}
                  data={tableData}
                  renderTopToolbarCustomActions={() => actions}
                  initialState={{density: 'compact'}}
                  enablePagination={true}
                  enableBottomToolbar={true}
                  muiTableProps={{
                    sx: {
                      tableLayout: 'fixed',
                      width: "800px",
                    },
                  }}
                />
              </div>
            </TabPanel>
          </div>
        </ArgosFlexbox>
      </TabContext>
      <div style={{paddingRight: '10px', height: '50%', width: '100%'}}>
        <ArgosTableSimple
          columns={fovAndBoresightRadarTableColumns}
          data={fovAndBoresightRadarTableData}
          initialState={{density: 'compact'}}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          enablePagination={false}
        />
      </div>
    </div>
  )
}
